import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
    DndContext,
    DragOverlay,
    MouseSensor,
    TouchSensor,
    pointerWithin,
    useDraggable,
    useDroppable,
    useSensor,
    useSensors,
} from "@dnd-kit/core";
import { MagnifyingGlassIcon, XMarkIcon } from "@heroicons/react/20/solid";
import {
    AKFA_PAGE_BASE,
    getAkfaMappingAkfaProducts,
    getAkfaWinproProducts,
    getAkfaWinproTextureOptions,
    saveAkfaMappingsBatch,
} from "../../../services/akfa/akfaService";
import { formatPrice, showError } from "../../../layout/akfa/akfaUtils";
import ConflictBanner from "./ConflictBanner";
import useMappingDraft, {
    akfaLabel,
    defaultAkfaQuery,
    idEq,
    normalizeAkfaItem,
    normalizeTextureOption,
    useDebouncedValue,
    usePagedList,
} from "./useMappingDraft";

// Spetsifikatsiya: FRONTEND_Plan_4.md, 3-bo'lim. Bitta komponent, 2 rejim:
//  mode="product" — bitta mahsulot teksturalari; mode="global" — barcha mahsulotlar teksturalari (guruhlangan)

const PAGE_SIZE = 50;
// Global rejim: har bir mahsulot uchun alohida texture-options so'rovi ketadi — sahifa kichikroq
const PRODUCT_PAGE_SIZE = 20;

// GET /winpro-products/{productId}/texture-options → tekstura qatorlari
async function fetchTextureRows(token, product) {
    const response = await getAkfaWinproTextureOptions(token, product.productId);
    if (!response.success) return { ...response, rows: [] };
    const options = Array.isArray(response.data) ? response.data : response.data?.data || [];
    return { success: true, rows: options.map((option) => normalizeTextureOption(option, product)) };
}
const SCROLL_THRESHOLD = 120;

function classNames(...classes) {
    return classes.filter(Boolean).join(" ");
}

const toastOpts = { position: "bottom-right", autoClose: 2600 };

function GripIcon() {
    return (
        <svg width="12" height="16" viewBox="0 0 12 16" fill="currentColor" aria-hidden="true">
            <circle cx="3" cy="3" r="1.4" />
            <circle cx="9" cy="3" r="1.4" />
            <circle cx="3" cy="8" r="1.4" />
            <circle cx="9" cy="8" r="1.4" />
            <circle cx="3" cy="13" r="1.4" />
            <circle cx="9" cy="13" r="1.4" />
        </svg>
    );
}

// Sichqoncha — butun qatordan; barmoq — faqat ⠿ tutqichdan (qolgan joyda ro'yxat oddiy scroll bo'ladi)
function useDragDrop(dragId, dropId, data, dropEnabled) {
    const drag = useDraggable({ id: dragId, data });
    const drop = useDroppable({ id: dropId, data, disabled: !dropEnabled });
    const setRef = useCallback(
        (node) => {
            drag.setNodeRef(node);
            drop.setNodeRef(node);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [drag.setNodeRef, drop.setNodeRef]
    );
    const rowProps = {
        ref: setRef,
        onMouseDown: (e) => {
            if (e.target.closest("button, input, label")) return;
            drag.listeners?.onMouseDown?.(e);
        },
    };
    const handleProps = {
        onTouchStart: (e) => drag.listeners?.onTouchStart?.(e),
        style: { touchAction: "none" },
    };
    return { rowProps, handleProps, isDragging: drag.isDragging, isOver: drop.isOver };
}

function SearchField({ value, onChange, placeholder, inputRef }) {
    return (
        <label className="flex flex-1 basis-40 min-w-0 items-center gap-1.5 border border-gray-300 rounded-md px-2.5 bg-white focus-within:ring-1 focus-within:ring-indigo-500">
            <MagnifyingGlassIcon className="w-4 h-4 flex-none text-gray-400" />
            <input
                ref={inputRef}
                type="text"
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder}
                aria-label={placeholder}
                className="w-full min-w-0 border-0 p-0 py-1.5 text-sm focus:ring-0"
            />
        </label>
    );
}

function OnlyUnmapped({ checked, onChange }) {
    const { t } = useTranslation();
    return (
        <label className="inline-flex items-center gap-1.5 text-xs text-gray-500 whitespace-nowrap cursor-pointer">
            <input
                type="checkbox"
                checked={checked}
                onChange={(e) => onChange(e.target.checked)}
                className="w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
            />
            {t("akfaIntegrationPage.mappingModal.only_unmapped")}
        </label>
    );
}

function TextureRow({ row, akfa, checked, dragKind, onToggle, onUnlink }) {
    const { t } = useTranslation();
    const { rowProps, handleProps, isDragging, isOver } = useDragDrop(
        `tex:${row.key}`,
        `drop-tex:${row.key}`,
        { kind: "tex", key: row.key },
        dragKind === "akfa"
    );
    return (
        <div
            {...rowProps}
            onClick={(e) => {
                if (e.target.closest("button, input, label")) return;
                onToggle(row.key);
            }}
            className={classNames(
                "flex items-center gap-2.5 px-2.5 py-2 border rounded-lg select-none cursor-grab",
                isOver
                    ? "border-indigo-600 border-solid bg-indigo-50"
                    : dragKind === "akfa"
                    ? "border-gray-300 border-dashed"
                    : "border-transparent",
                !isOver && (checked ? "bg-indigo-50" : "hover:bg-slate-50"),
                isDragging && "opacity-40"
            )}
        >
            <input
                type="checkbox"
                checked={checked}
                onChange={() => onToggle(row.key)}
                aria-label={t("akfaIntegrationPage.mappingModal.check_aria", { name: row.textureName })}
                className="w-4 h-4 flex-none rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
            />
            <span {...handleProps} className="flex-none inline-flex text-gray-400 p-1.5 -m-1.5 cursor-grab">
                <GripIcon />
            </span>
            <span className="font-semibold text-sm text-gray-900 min-w-[120px]">{row.textureName}</span>
            {row.price != null && (
                <span className="flex-none font-mono text-xs text-gray-500 whitespace-nowrap">
                    {formatPrice(row.price, row.currency)}
                </span>
            )}
            <span className="text-gray-400" aria-hidden="true">
                &rarr;
            </span>
            {akfa ? (
                <span className="inline-flex items-center gap-1.5 min-w-0 bg-green-50 text-green-700 rounded-md pl-2 pr-0.5 py-0.5 text-xs">
                    <span className="truncate">
                        <b>{akfa.externalId ?? `#${akfa.id}`}</b> {akfa.name}
                    </span>
                    <button
                        type="button"
                        onClick={() => onUnlink(row.key)}
                        aria-label={t("akfaIntegrationPage.mappingModal.unlink_aria")}
                        className="flex-none w-6 h-6 rounded hover:bg-black/10"
                    >
                        &times;
                    </button>
                </span>
            ) : (
                <span className="flex-1 min-w-0 truncate border border-dashed border-gray-300 rounded-md px-2 py-1 text-xs text-gray-400">
                    {t("akfaIntegrationPage.mappingModal.drop_here")}
                </span>
            )}
        </div>
    );
}

function AkfaRow({ item, chips, global, dragKind, onUnlink }) {
    const { t } = useTranslation();
    const { rowProps, handleProps, isDragging, isOver } = useDragDrop(
        `akfa:${item.id}`,
        `drop-akfa:${item.id}`,
        { kind: "akfa", akfaId: item.id },
        dragKind === "tex"
    );
    return (
        <div
            {...rowProps}
            className={classNames(
                "flex items-start gap-2.5 px-2.5 py-2 border rounded-lg select-none cursor-grab",
                isOver
                    ? "border-indigo-600 border-solid bg-indigo-50"
                    : dragKind === "tex"
                    ? "border-gray-300 border-dashed"
                    : "border-transparent hover:bg-slate-50",
                isDragging && "opacity-40"
            )}
        >
            <span {...handleProps} className="flex-none inline-flex text-gray-400 p-1.5 -m-1.5 mt-0 cursor-grab">
                <GripIcon />
            </span>
            <div className="flex-1 min-w-0">
                <div className="text-sm truncate">
                    <span className="font-mono text-xs text-gray-500">{item.externalId ?? `#${item.id}`}</span> {item.name}
                    {item.price != null && (
                        <span className="ml-1.5 font-mono text-xs text-gray-500 whitespace-nowrap">
                            {formatPrice(item.price, item.currency)}
                        </span>
                    )}
                </div>
                {item.groupName && (
                    <div className="mt-1">
                        <span className="bg-gray-100 text-gray-500 rounded-md px-1.5 py-0.5 text-xs whitespace-nowrap">
                            {item.groupName}
                        </span>
                    </div>
                )}
                {chips.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1.5">
                        {chips.map((chip) => (
                            <span
                                key={chip.key}
                                className="inline-flex items-center gap-0.5 bg-indigo-50 text-indigo-700 rounded-md pl-2 pr-0.5 py-px text-xs font-semibold"
                            >
                                {chip.textureName}
                                {global && chip.productName ? ` · ${chip.productName}` : ""}
                                <button
                                    type="button"
                                    onClick={() => onUnlink(chip.key)}
                                    aria-label={t("akfaIntegrationPage.mappingModal.unlink_chip_aria", {
                                        name: chip.textureName,
                                    })}
                                    className="w-5 h-5 rounded hover:bg-black/10"
                                >
                                    &times;
                                </button>
                            </span>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

function ListStatus({ loading, done, empty, emptyText }) {
    const { t } = useTranslation();
    if (loading) return <div className="py-4 text-center text-sm text-gray-400">{t("akfaIntegrationPage.common.loading")}</div>;
    if (empty && done) return <div className="py-8 text-center text-sm text-gray-400">{emptyText}</div>;
    return null;
}

const onScrollEnd = (loadMore) => (e) => {
    const el = e.currentTarget;
    if (el.scrollTop + el.clientHeight > el.scrollHeight - SCROLL_THRESHOLD) loadMore();
};

/**
 * mode: "product" | "global"
 * product: { productId, productName, article } — product rejimida
 * onClose(), onSaved() — saqlangandan keyin jadvallar va statistika yangilanadi
 */
export default function AkfaMappingModal({ mode, product, onClose, onSaved }) {
    const { t } = useTranslation();
    const { token } = useSelector((state) => state.user);
    const isGlobal = mode === "global";
    const productId = product?.productId;

    const draft = useMappingDraft();
    const { dispatch } = draft;

    const productInfo = product || {};
    const [productRows, setProductRows] = useState({ loading: !isGlobal, items: [] });

    const [akfaQuery, setAkfaQuery] = useState(isGlobal ? "" : defaultAkfaQuery(product?.productName));
    const [akfaUnmapped, setAkfaUnmapped] = useState(false);
    const [texQuery, setTexQuery] = useState("");
    const [texUnmapped, setTexUnmapped] = useState(false);
    const debouncedAkfaQuery = useDebouncedValue(akfaQuery.trim());
    const debouncedTexQuery = useDebouncedValue(texQuery.trim());

    const [confirmClose, setConfirmClose] = useState(false);
    const [conflict, setConflict] = useState(null);
    const [saving, setSaving] = useState(false);
    const [active, setActive] = useState(null);

    const activeRef = useRef(null);
    const justDraggedRef = useRef(false);
    const overlayDownRef = useRef(false);
    const texSearchRef = useRef(null);
    const closeBtnRef = useRef(null);
    const texListRef = useRef(null);
    const akfaListRef = useRef(null);

    // ---------- Ma'lumot yuklash ----------

    useEffect(() => {
        if (isGlobal) return undefined;
        let cancelled = false;
        fetchTextureRows(token, product).then((result) => {
            if (cancelled) return;
            if (!result.success) showError(result, t("akfaIntegrationPage.common.load_error"));
            dispatch({ type: "textures", rows: result.rows });
            setProductRows({ loading: false, items: result.rows });
        });
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isGlobal, token, productId]);

    const textureList = usePagedList(
        async (page) => {
            // Alohida endpoint yo'q: mahsulotlar sahifasi + har biri uchun texture-options (FRONTEND_Plan_4, 3.4)
            const response = await getAkfaWinproProducts(token, {
                search: debouncedTexQuery,
                page: page + AKFA_PAGE_BASE,
                size: PRODUCT_PAGE_SIZE,
            });
            if (!response.success) return response;
            const products = (response.data?.data || []).map((item) => ({
                productId: item.productId,
                productName: item.productName || "",
                article: item.article || "",
            }));
            const results = await Promise.all(products.map((item) => fetchTextureRows(token, item)));
            const failed = results.find((result) => !result.success);
            if (failed) return failed;
            const total = response.data?.total ?? products.length;
            return {
                ...response,
                data: {
                    data: results.flatMap((result) => result.rows),
                    total,
                    hasMore: products.length === PRODUCT_PAGE_SIZE && (page + 1) * PRODUCT_PAGE_SIZE < total,
                },
            };
        },
        [token, debouncedTexQuery],
        {
            enabled: isGlobal,
            size: PRODUCT_PAGE_SIZE,
            onItems: (rows) => dispatch({ type: "textures", rows }),
        }
    );

    const akfaList = usePagedList(
        async (page) => {
            const response = await getAkfaMappingAkfaProducts(token, {
                search: debouncedAkfaQuery,
                linkStatus: akfaUnmapped ? "unmapped" : "all",
                page: page + AKFA_PAGE_BASE,
                size: PAGE_SIZE,
            });
            if (!response.success) return response;
            const items = (response.data?.data || []).map(normalizeAkfaItem);
            return { ...response, data: { ...response.data, data: items } };
        },
        [token, debouncedAkfaQuery, akfaUnmapped],
        { size: PAGE_SIZE, onItems: (items) => dispatch({ type: "akfaItems", items }) }
    );

    useEffect(() => {
        if (textureList.error) showError(textureList.error, t("akfaIntegrationPage.common.load_error"));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [textureList.error]);

    useEffect(() => {
        if (akfaList.error) showError(akfaList.error, t("akfaIntegrationPage.common.load_error"));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [akfaList.error]);

    // ---------- Ko'rinadigan ro'yxatlar ----------

    const scopeRows = isGlobal ? textureList.items : productRows.items;

    const visibleTextures = useMemo(
        () => (isGlobal && texUnmapped ? scopeRows.filter((row) => draft.valueOf(row.key) == null) : scopeRows),
        [isGlobal, texUnmapped, scopeRows, draft]
    );

    const inScope = useCallback((key) => isGlobal || idEq(draft.meta[key]?.productId, productId), [
        isGlobal,
        draft.meta,
        productId,
    ]);

    // Server javobi + draft birlashtiriladi
    const chipsFor = useCallback(
        (item) => {
            const keys = new Set();
            item.linked.forEach((row) => {
                if (idEq(draft.valueOf(row.key), item.id)) keys.add(row.key);
            });
            Object.entries(draft.edits).forEach(([key, value]) => {
                if (idEq(value, item.id)) keys.add(key);
            });
            return [...keys]
                .filter(inScope)
                .map((key) => ({ key, ...draft.meta[key] }));
        },
        [draft, inScope]
    );

    const visibleAkfa = useMemo(() => {
        const items = akfaList.items.map((item) => ({ item, chips: chipsFor(item) }));
        return akfaUnmapped ? items.filter((entry) => entry.chips.length === 0) : items;
    }, [akfaList.items, chipsFor, akfaUnmapped]);

    const linkedCount = scopeRows.filter((row) => draft.valueOf(row.key) != null).length;

    // Ro'yxat scroll bo'ladigan darajada to'lmagan bo'lsa (masalan "Bog'lanmagan" filtri ko'p qatorni yashirsa) —
    // keyingi sahifa avtomatik yuklanadi, aks holda infinite scroll hech qachon ishga tushmaydi
    useEffect(() => {
        const fill = (el, list) => {
            if (el && !list.loading && !list.done && el.scrollHeight <= el.clientHeight + SCROLL_THRESHOLD) {
                list.loadMore();
            }
        };
        if (isGlobal) fill(texListRef.current, textureList);
        fill(akfaListRef.current, akfaList);
    });

    // ---------- Yopish ----------

    const requestClose = useCallback(() => {
        if (draft.changeCount > 0) setConfirmClose(true);
        else onClose();
    }, [draft.changeCount, onClose]);

    useEffect(() => {
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        (isGlobal ? texSearchRef : closeBtnRef).current?.focus();
        return () => {
            document.body.style.overflow = prevOverflow;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const onKeyDown = (e) => {
            if (e.key !== "Escape") return;
            // Sudrash paytida Escape — faqat sudrashni bekor qiladi (dnd-kit o'zi bekor qiladi)
            if (activeRef.current) return;
            requestClose();
        };
        document.addEventListener("keydown", onKeyDown);
        return () => document.removeEventListener("keydown", onKeyDown);
    }, [requestClose]);

    // ---------- Drag & drop ----------

    const sensors = useSensors(
        useSensor(MouseSensor, { activationConstraint: { distance: 6 } }),
        useSensor(TouchSensor, { activationConstraint: { distance: 6 } })
    );

    const finishDrag = () => {
        activeRef.current = null;
        setActive(null);
        justDraggedRef.current = true;
        setTimeout(() => {
            justDraggedRef.current = false;
        }, 0);
    };

    const handleDragStart = ({ active: current }) => {
        activeRef.current = current.data.current;
        setActive(current.data.current);
    };

    const handleDragEnd = ({ active: current, over }) => {
        const source = current.data.current;
        finishDrag();
        if (!over) return;
        const target = over.data.current;
        let keys;
        let akfaId;
        if (source.kind === "tex" && target.kind === "akfa") {
            keys = draft.keysWithChecked(source.key);
            akfaId = target.akfaId;
        } else if (source.kind === "akfa" && target.kind === "tex") {
            keys = draft.keysWithChecked(target.key);
            akfaId = source.akfaId;
        } else {
            return;
        }
        // Allaqachon ulangan teksturaga yangi Akfa tashlansa — almashtiriladi (N:1)
        dispatch({ type: "assign", keys, akfaId });
        setConfirmClose(false);
        toast.info(
            t("akfaIntegrationPage.mappingModal.dropped", {
                count: keys.length,
                akfa: akfaLabel(draft.akfa[akfaId]),
            }),
            toastOpts
        );
    };

    const dragLabel = () => {
        if (!active) return "";
        if (active.kind === "akfa") return akfaLabel(draft.akfa[active.akfaId]);
        const keys = draft.keysWithChecked(active.key);
        const first = draft.meta[keys[0]]?.textureName || "";
        return keys.length > 1 ? `${first} +${keys.length - 1}` : first;
    };

    // ---------- Saqlash va 409 ----------

    const fetchServerRows = async (productIds) => {
        // texture-options mahsulot nomini qaytarmaydi — meta dan olinadi
        const products = productIds.map((id) => {
            if (!isGlobal && idEq(id, productId)) return { ...product, ...productInfo, productId: id };
            const meta = Object.values(draft.meta).find((m) => idEq(m.productId, id));
            return { productId: id, productName: meta?.productName || "" };
        });
        const results = await Promise.all(products.map((item) => fetchTextureRows(token, item)));
        if (results.some((result) => !result.success)) return null;
        return results.flatMap((result) => result.rows);
    };

    const affectedProductIds = (changes) => {
        const ids = new Map();
        changes.forEach((change) => ids.set(String(change.productId), change.productId));
        if (!isGlobal && productId != null) ids.set(String(productId), productId);
        return [...ids.values()];
    };

    const handleConflict = async (changes, body) => {
        const rows = await fetchServerRows(affectedProductIds(changes));
        if (!rows) {
            showError(null, t("akfaIntegrationPage.common.load_error"));
            return;
        }
        const server = Object.fromEntries(rows.map((row) => [row.key, row]));
        let keys = draft.changeKeys.filter((key) => !idEq(server[key]?.akfaProductId, draft.orig[key] ?? null));
        if (keys.length === 0 && Array.isArray(body?.conflicts)) {
            keys = body.conflicts.map((c) => `${c.productId}:${c.textureId}`);
        }
        if (keys.length === 0) {
            showError({ message: body?.message }, t("akfaIntegrationPage.mappingModal.conflict_generic"));
            return;
        }
        setConfirmClose(false);
        setConflict({
            keys,
            items: keys.map((key) => {
                const meta = server[key] || draft.meta[key] || {};
                return {
                    key,
                    productName: meta.productName || draft.meta[key]?.productName || "",
                    textureName: meta.textureName || draft.meta[key]?.textureName || "",
                    current: server[key]?.akfa
                        ? { ...server[key].akfa, ...draft.akfa[server[key].akfa.id] }
                        : null,
                };
            }),
        });
    };

    const save = async () => {
        if (draft.changeCount === 0 || saving) return;
        const changes = draft.toPayload();
        setSaving(true);
        const response = await saveAkfaMappingsBatch(token, changes);
        if (response.success) {
            setSaving(false);
            const name = isGlobal ? t("akfaIntegrationPage.mappingModal.title_global") : productInfo.productName;
            toast.success(
                t("akfaIntegrationPage.mappingModal.saved", { name, count: changes.length }),
                { position: "bottom-right", autoClose: 3000 }
            );
            onSaved?.();
            onClose();
            return;
        }
        if (response.status === 409) {
            await handleConflict(changes, response.data);
        } else {
            showError(response, t("akfaIntegrationPage.common.action_error"));
        }
        setSaving(false);
    };

    // "Yangilash va davom etish": orig = server; to'qnashganlar server qiymatiga qaytadi, qolganlar draft da qoladi
    const refreshAndContinue = async () => {
        const rows = await fetchServerRows(affectedProductIds(draft.toPayload()));
        if (!rows) {
            showError(null, t("akfaIntegrationPage.common.load_error"));
            return;
        }
        dispatch({ type: "serverState", rows, conflictKeys: conflict.keys });
        if (!isGlobal) {
            setProductRows({ loading: false, items: rows.filter((row) => idEq(row.productId, productId)) });
        } else {
            textureList.reload();
        }
        akfaList.reload();
        setConflict(null);
        toast.info(t("akfaIntegrationPage.mappingModal.refreshed"), toastOpts);
    };

    // ---------- Render ----------

    const title = isGlobal ? t("akfaIntegrationPage.mappingModal.title_global") : productInfo.productName || "";
    const subtitle = isGlobal
        ? t("akfaIntegrationPage.mappingModal.sub_global", { linked: linkedCount, total: scopeRows.length })
        : t("akfaIntegrationPage.mappingModal.sub_product", {
              article: productInfo.article || "—",
              linked: linkedCount,
              total: scopeRows.length,
          });

    const checkedCount = draft.checkedKeys.length;
    const dragKind = active?.kind || null;
    let lastGroup = null;

    const content = (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center p-5 bg-slate-900/50"
            onMouseDown={(e) => {
                overlayDownRef.current = e.target === e.currentTarget;
            }}
            onClick={(e) => {
                // Sudrab tugatilgandan keyingi click overlay ni yopmasligi kerak
                if (justDraggedRef.current || !overlayDownRef.current || e.target !== e.currentTarget) return;
                requestClose();
            }}
        >
            <div
                role="dialog"
                aria-modal="true"
                aria-labelledby="akfa-mapping-title"
                className="flex flex-col w-full max-w-[1120px] h-[min(760px,calc(100vh-40px))] bg-white rounded-xl shadow-2xl overflow-hidden"
            >
                <div className="flex items-start justify-between gap-3 px-4 py-4 border-b border-gray-200">
                    <div className="min-w-0">
                        <h2 id="akfa-mapping-title" className="text-lg font-semibold text-gray-900 truncate">
                            {title}
                        </h2>
                        <div className="text-xs text-gray-500 mt-0.5">{subtitle}</div>
                    </div>
                    <button
                        ref={closeBtnRef}
                        type="button"
                        onClick={requestClose}
                        aria-label={t("akfaIntegrationPage.common.close")}
                        className="flex-none inline-flex items-center justify-center w-10 h-10 rounded-lg text-gray-500 hover:bg-slate-50 hover:text-gray-900"
                    >
                        <XMarkIcon className="w-6 h-6" />
                    </button>
                </div>

                {conflict && (
                    <ConflictBanner
                        title={t("akfaIntegrationPage.mappingModal.conflict_title", { count: conflict.items.length })}
                        text={t("akfaIntegrationPage.mappingModal.conflict_text")}
                        items={conflict.items}
                        renderItem={(item) => (
                            <>
                                {item.productName} · {item.textureName} —{" "}
                                {t("akfaIntegrationPage.mappingModal.conflict_now")}{" "}
                                {item.current
                                    ? akfaLabel(item.current)
                                    : t("akfaIntegrationPage.mapping.winpro_unmapped").toLowerCase()}
                            </>
                        )}
                        action={
                            <button
                                type="button"
                                onClick={refreshAndContinue}
                                className="px-4 py-2 text-sm font-semibold text-white bg-slate-800 rounded-md hover:bg-slate-700"
                            >
                                {t("akfaIntegrationPage.mappingModal.conflict_refresh")}
                            </button>
                        }
                    />
                )}

                <DndContext
                    sensors={sensors}
                    collisionDetection={pointerWithin}
                    onDragStart={handleDragStart}
                    onDragEnd={handleDragEnd}
                    onDragCancel={finishDrag}
                >
                    <div
                        className={classNames(
                            "flex-1 min-h-0 grid grid-cols-1 grid-rows-2 md:grid-cols-2 md:grid-rows-1",
                            dragKind && "cursor-grabbing"
                        )}
                    >
                        {/* Chap — WinPro teksturalari */}
                        <div className="flex flex-col min-h-0 min-w-0">
                            <div className="flex flex-wrap items-center gap-2.5 min-h-[54px] px-3.5 py-2.5 border-b border-gray-200">
                                {isGlobal ? (
                                    <SearchField
                                        inputRef={texSearchRef}
                                        value={texQuery}
                                        onChange={setTexQuery}
                                        placeholder={t("akfaIntegrationPage.mappingModal.tex_search")}
                                    />
                                ) : (
                                    <span className="flex-1 text-xs font-semibold uppercase tracking-wide text-gray-400">
                                        {t("akfaIntegrationPage.mappingModal.textures_label")}
                                    </span>
                                )}
                                {checkedCount > 0 && (
                                    <span className="font-mono text-xs text-gray-500">
                                        {t("akfaIntegrationPage.mappingModal.checked", { count: checkedCount })}
                                    </span>
                                )}
                                {isGlobal && <OnlyUnmapped checked={texUnmapped} onChange={setTexUnmapped} />}
                            </div>
                            <div
                                className="flex-1 overflow-y-auto p-2 flex flex-col gap-0.5"
                                ref={texListRef}
                                onScroll={isGlobal ? onScrollEnd(textureList.loadMore) : undefined}
                            >
                                {visibleTextures.map((row) => {
                                    const value = draft.valueOf(row.key);
                                    const header =
                                        isGlobal && !idEq(row.productId, lastGroup) ? (
                                            <div className="px-2.5 pt-3 pb-1 first:pt-1 text-xs font-bold text-gray-500">
                                                {row.productName}
                                            </div>
                                        ) : null;
                                    if (isGlobal) lastGroup = row.productId;
                                    return (
                                        <React.Fragment key={row.key}>
                                            {header}
                                            <TextureRow
                                                row={row}
                                                akfa={value != null ? draft.akfa[value] || { id: value } : null}
                                                checked={Boolean(draft.checked[row.key])}
                                                dragKind={dragKind}
                                                onToggle={(key) => {
                                                    // Sudrab, shu qatorning o'ziga tashlanganda keladigan click — belgilamaydi
                                                    if (!justDraggedRef.current) dispatch({ type: "toggleCheck", key });
                                                }}
                                                onUnlink={(key) => {
                                                    dispatch({ type: "unlink", key });
                                                    setConfirmClose(false);
                                                }}
                                            />
                                        </React.Fragment>
                                    );
                                })}
                                <ListStatus
                                    loading={isGlobal ? textureList.loading : productRows.loading}
                                    done={isGlobal ? textureList.done : !productRows.loading}
                                    empty={visibleTextures.length === 0}
                                    emptyText={
                                        isGlobal
                                            ? t("akfaIntegrationPage.mappingModal.no_textures_found")
                                            : t("akfaIntegrationPage.mappingModal.no_textures_in_product")
                                    }
                                />
                            </div>
                        </div>

                        {/* O'ng — Akfa tovarlari */}
                        <div className="flex flex-col min-h-0 min-w-0 border-t md:border-t-0 md:border-l border-gray-200">
                            <div className="flex flex-wrap items-center gap-2.5 min-h-[54px] px-3.5 py-2.5 border-b border-gray-200">
                                <span className="text-xs font-semibold uppercase tracking-wide text-gray-400">
                                    {t("akfaIntegrationPage.mapping.subtab_akfa")}
                                </span>
                                <SearchField
                                    value={akfaQuery}
                                    onChange={setAkfaQuery}
                                    placeholder={t("akfaIntegrationPage.mappingModal.akfa_search")}
                                />
                                <OnlyUnmapped checked={akfaUnmapped} onChange={setAkfaUnmapped} />
                            </div>
                            <div
                                ref={akfaListRef}
                                className="flex-1 overflow-y-auto p-2 flex flex-col gap-0.5"
                                onScroll={onScrollEnd(akfaList.loadMore)}
                            >
                                {visibleAkfa.map(({ item, chips }) => (
                                    <AkfaRow
                                        key={item.id}
                                        item={item}
                                        chips={chips}
                                        global={isGlobal}
                                        dragKind={dragKind}
                                        onUnlink={(key) => {
                                            dispatch({ type: "unlink", key });
                                            setConfirmClose(false);
                                        }}
                                    />
                                ))}
                                <ListStatus
                                    loading={akfaList.loading}
                                    done={akfaList.done}
                                    empty={visibleAkfa.length === 0}
                                    emptyText={t("akfaIntegrationPage.mappingModal.no_akfa_found")}
                                />
                            </div>
                        </div>
                    </div>

                    <DragOverlay dropAnimation={null}>
                        {active ? (
                            <div className="pointer-events-none inline-block max-w-[320px] truncate px-3 py-1.5 rounded-lg bg-slate-800 text-slate-50 text-xs font-semibold shadow-lg cursor-grabbing">
                                {dragLabel()}
                            </div>
                        ) : null}
                    </DragOverlay>
                </DndContext>

                <div className="flex flex-wrap items-center gap-2.5 px-4 py-3 border-t border-gray-200">
                    {confirmClose ? (
                        <>
                            <span className="flex-1 min-w-[220px] text-sm font-semibold text-amber-800">
                                {t("akfaIntegrationPage.mappingModal.unsaved_warning", { count: draft.changeCount })}
                            </span>
                            <button
                                type="button"
                                onClick={() => setConfirmClose(false)}
                                className="px-4 py-2 text-sm font-semibold text-gray-900 bg-white border border-gray-300 rounded-md hover:border-indigo-600 hover:text-indigo-600"
                            >
                                {t("akfaIntegrationPage.mappingModal.stay")}
                            </button>
                            <button
                                type="button"
                                onClick={onClose}
                                className="px-4 py-2 text-sm font-semibold text-white bg-red-700 rounded-md hover:bg-red-600"
                            >
                                {t("akfaIntegrationPage.mappingModal.discard")}
                            </button>
                        </>
                    ) : (
                        <>
                            <span className="flex-1 min-w-[220px] text-xs text-gray-500">
                                {draft.changeCount > 0 && (
                                    <>
                                        <b className="text-gray-900">
                                            {t("akfaIntegrationPage.mappingModal.changes", { count: draft.changeCount })}
                                        </b>{" "}
                                        ·{" "}
                                    </>
                                )}
                                {t("akfaIntegrationPage.mappingModal.hint")}
                            </span>
                            <button
                                type="button"
                                onClick={requestClose}
                                className="px-1 py-2 text-sm font-semibold text-gray-500 underline"
                            >
                                {t("akfaIntegrationPage.common.cancel")}
                            </button>
                            <button
                                type="button"
                                onClick={save}
                                disabled={draft.changeCount === 0 || saving}
                                className="px-4 py-2 text-sm font-semibold text-white bg-slate-800 rounded-md hover:bg-slate-700 disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                                {saving ? t("akfaIntegrationPage.modal.saving") : t("akfaIntegrationPage.common.save")}
                            </button>
                        </>
                    )}
                </div>
            </div>
        </div>
    );

    return createPortal(content, document.body);
}
